import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Box } from "@mui/material";
import { useAuth } from "../../../context/AuthProvider";
import ProspectProfile from "./ProspectProfile";
import ProspectInteractions from "./ProspectInteractions";
import ProspectStatus from "./ProspectStatus";
import ProspectsTable from "./ProspectsTable";
import ProspectForm from "./ProspectForm";
import SearchBar from "./SearchBar";
import ArchiveProspectsGrid from "./ArchiveGrid"; 

export default function Prospects() {
  const [prospects, setProspects] = useState([]);
  const { user } = useAuth();
  const [prospectsFiltres, setProspectsFiltres] = useState([]);
  const [selectedProspect, setSelectedProspect] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [currentProspect, setCurrentProspect] = useState(null);
  const [showArchives, setShowArchives] = useState(false); 
  const [openArchive, setOpenArchive] = useState(false);
  const navigate = useNavigate();


  useEffect(() => {
    fetchProspects();
  }, []);

  const BACKEND_URL =
    window.location.hostname === "localhost"
      ? process.env.REACT_APP_BACKEND_URL_LOCAL + "/api"
      : process.env.REACT_APP_BACKEND_URL_PROD + "/api";

  const fetchProspects = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${BACKEND_URL}/prospects`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setProspects(res.data);
      setProspectsFiltres(res.data);
    } catch (error) {
      console.error("Erreur lors de la récupération des prospects :", error);
    }
  };

  const supprimerProspect = async (id) => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${BACKEND_URL}/prospects/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      fetchProspects();
      setSelectedProspect(null);
    } catch (error) {
      console.error("Erreur lors de la suppression du prospect :", error);
    }
  };

  const handleOpenForm = (prospect = null) => {
    setCurrentProspect(prospect);
    setOpenModal(true);
  };

  const handleViewDetails = (id) => {
    navigate(`/prospects/${id}`);
  };  

  const handleSaveProspect = async (newProspect) => {
    const token = localStorage.getItem("token");
  
    if (newProspect._id) {
      const res = await axios.put(`${BACKEND_URL}/prospects/${newProspect._id}`, newProspect, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSelectedProspect(res.data);
    } else {
      await axios.post(`${BACKEND_URL}/prospects`, newProspect, {
        headers: { Authorization: `Bearer ${token}` },
      });
    }
  
    fetchProspects();
    setOpenModal(false);
    setSelectedProspect(null);
  };  

  const handleExport = () => {
    console.log("Exportation des prospects...");
  };

  const handleStatutUpdate = (newStatut) => {
    if (!selectedProspect) return;
    setSelectedProspect((prev) => ({ ...prev, statut: newStatut }));
  };  

  return (
    <Box sx={{ p: 4 }}>
      {/* 📋 Section Profil / Interactions / Statut */}
      {!showArchives && (
        <Box sx={{ display: "flex", gap: 2, mb: 4 }}>
          <ProspectProfile prospect={selectedProspect} onViewDetails={handleViewDetails} />
          <ProspectInteractions prospect={selectedProspect} />
          <ProspectStatus prospect={selectedProspect} onStatutUpdate={handleStatutUpdate} />
        </Box>
      )}

      {/* 🔍 Barre de recherche avec exportation & switch archives */}
      <SearchBar
        prospects={prospects}
        setProspectsFiltres={setProspectsFiltres}
        onAdd={() => handleOpenForm()}
        onExport={handleExport}
        onArchiveView={() => setOpenArchive(true)}
      />

      {/* 🗂️ Grille principale ou archive */}
      {showArchives ? (
        <ArchiveProspectsGrid prospects={prospects} refreshLeads={fetchProspects} />
      ) : (
        <ProspectsTable
          prospects={prospectsFiltres}
          onDelete={supprimerProspect}
          selectProspect={setSelectedProspect}
          onEdit={handleOpenForm}
          refreshProspects={fetchProspects}
        />
      )}

      {/* 📝 Formulaire Prospect */}
      <ProspectForm
        open={openModal}
        onClose={() => setOpenModal(false)}
        onSave={handleSaveProspect}
        prospect={currentProspect}
        referent={user?.Nom}
      />

      {openArchive && (
        <motion.div
          className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="bg-white rounded-lg shadow-xl w-[90vw] h-[80vh] p-6 relative"
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0.9 }}
          >
            {/* ❌ Bouton de fermeture */}
            <button
              className="absolute top-3 right-3 text-gray-500 hover:text-gray-800"
              onClick={() => setOpenArchive(false)}
            >
              ✕
            </button>

            {/* 📦 Grille d'archives des prospects */}
            <ArchiveProspectsGrid 
              prospects={prospects} 
              refreshProspects={fetchProspects} 
            />
          </motion.div>
        </motion.div>
      )}
    </Box>
  );
}
